import { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, Clock, ArrowRight, ArrowUpRight, Handshake } from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO, { SITE_URL, SITE_NAME, buildBreadcrumbJsonLd } from "@/components/SEO";
import { blogPosts, getBlogPostBySlug } from "@/data/blogPosts";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const BlogPost = () => {
  const { slug } = useParams();
  const post = getBlogPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const otherPosts = blogPosts.filter((p) => p.slug !== post.slug);
  const crumbs = [{ label: "Home", href: "/" }, { label: "Blog", href: "/blog" }, { label: post.category }];
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    image: `${SITE_URL}${post.heroImage}`,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/assets/logo.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
  };

  return (
    <>
      <SEO
        title={post.seoTitle}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        keywords={post.keywords}
        image={post.heroImage}
        type="article"
        jsonLd={[buildBreadcrumbJsonLd(crumbs), articleJsonLd]}
      />
      <PageHero
        title={post.title}
        crumbs={crumbs}
        lightfall
      />

      <article data-testid="blog-post-article" className="py-24 lg:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp} className="flex items-center gap-5 text-sm text-slate-400">
            <span className="flex items-center gap-1.5">
              <CalendarDays size={14} /> {formatDate(post.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={14} /> {post.readTime}
            </span>
            <span className="rounded-full bg-miti-blue/10 text-miti-blue px-3 py-1 text-xs font-semibold uppercase tracking-wide">
              {post.category}
            </span>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-8 rounded-3xl overflow-hidden border border-slate-200 shadow-xl shadow-slate-900/5"
          >
            <img src={post.heroImage} alt={post.title} className="w-full h-[280px] sm:h-[360px] object-cover" />
          </motion.div>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-8 text-lg text-slate-600 leading-relaxed italic border-l-2 border-miti-blue pl-5"
          >
            {post.excerpt}
          </motion.p>

          <div className="mt-10 space-y-10">
            {post.sections.map((section, i) => (
              <motion.div
                key={section.heading}
                {...fadeUp}
                transition={{ duration: 0.6, delay: Math.min(i, 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`blog-post-section-${i + 1}`}
              >
                <h2 className="font-heading font-bold text-2xl text-slate-900 leading-snug">{section.heading}</h2>
                <div className="mt-4 space-y-4">
                  {section.paragraphs.map((p, j) => (
                    <p key={j} className="text-base md:text-lg text-slate-600 leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
                {section.bullets && (
                  <ul className="mt-4 space-y-2.5 list-disc pl-5 marker:text-miti-blue">
                    {section.bullets.map((b, j) => (
                      <li key={j} className="text-base md:text-lg text-slate-600 leading-relaxed">
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div
            {...fadeUp}
            className="mt-14 rounded-2xl border border-slate-200 bg-slate-50/70 p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6"
          >
            <div>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-miti-blue">
                Related Service
              </span>
              <h3 className="mt-2 font-heading font-bold text-xl text-slate-900">{post.relatedService.label}</h3>
            </div>
            <a
              href="/contact"
              data-testid="blog-post-contact-button"
              className="group shrink-0 inline-flex items-center gap-3 rounded-lg bg-miti-blue px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#005a96] transition-colors"
            >
              <Handshake size={17} />
              Talk to an Expert
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
        </div>
      </article>

      <section data-testid="blog-post-more-articles" className="pb-24 lg:pb-16 bg-slate-50/70 pt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.h2 {...fadeUp} className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 mb-8">
            More Articles
          </motion.h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {otherPosts.map((p, i) => (
              <motion.a
                key={p.slug}
                href={`/blog/${p.slug}`}
                data-testid={`blog-post-related-${p.slug}`}
                {...fadeUp}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                className="group rounded-2xl border border-slate-200 bg-white p-7 hover:border-miti-blue/40 hover:shadow-lg transition-all duration-300"
              >
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-miti-blue">
                  {p.category}
                </span>
                <h3 className="mt-3 font-heading font-semibold text-lg text-slate-900 leading-snug group-hover:text-miti-blue transition-colors">
                  {p.title}
                </h3>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-miti-blue">
                  Read Article
                  <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPost;
